import { apiClient as api } from './apiClient';

/**
 * Panel interno «Encuestas CX» (módulo CXCP_ENCUESTAS) — uso HUMANO desde Gescom.aux.
 *
 * A diferencia del resto de `cx/*` (App Roles de aplicación para n8n y el gateway de Mystery), estas
 * rutas van con el token delegado del usuario (interceptor MSAL de `apiClient`) y se autorizan por
 * la ACL de grupos de Entra: recurso `Cx.Panel.Read` para leer, `Cx.EncuestasF4.Visibilidad.Write`
 * para el interruptor de visibilidad F4 y `Cx.Bandeja.Write` para aprobar o descartar envíos.
 *
 * Contrato: `07_MODULOS/CXCP_ENCUESTAS/90_DOCUMENTACION/API_CX_CONTRATO.md`, sección «Panel interno».
 * Mientras el backend no la publique, la forma de las respuestas es la propuesta aquí; el
 * normalizador acepta tanto un array plano como el objeto envolvente, para no romper si el backend
 * elige la otra.
 */

/** Tipos de la campaña compradora semanal, en orden de prioridad (ROC > OC > SI). */
export type TipoCompra = 'ROC' | 'OC' | 'SI';

/** Un destinatario de la campaña de la semana (vista previa, sin reservar nada). */
export interface CandidatoSemana {
  tipo: TipoCompra;
  idCliente: number;
  codigoCliente?: string | null;
  nombre?: string | null;
  /** Email completo: CxCp lo necesita leer. Vacío si el cliente no tiene email en Gescom. */
  email?: string | null;
  /** Día del registro que origina el envío (actividad ROC u oportunidad), YYYY-MM-DD. */
  fechaRegistro?: string | null;
  idOperacion?: number | null;
  /** Nombre de la operación (`GC_OPERACION.DS_ALIAS`). */
  nombreOperacion?: string | null;
  /** CCL de la operación (`GC_OPERACION.DS_CCL`). */
  ccl?: string | null;
  idOporCompra?: number | null;
  idActividad?: number | null;
}

export interface CampanaSemana {
  /** Lunes de la semana consultada (YYYY-MM-DD). */
  lunes: string;
  candidatos: CandidatoSemana[];
}

type ConLista<K extends string, T> = T[] | ({ [P in K]?: T[] } & Record<string, unknown>);

/** Vista previa de la campaña compradora de la semana `lunes` → `lunes+6`. No envía ni registra. */
export const getCampanaSemana = async (lunes: string): Promise<CampanaSemana> => {
  const { data } = await api.get<ConLista<'candidatos', CandidatoSemana>>(
    `/api/cx/panel/semana?lunes=${encodeURIComponent(lunes)}`,
  );
  const candidatos = Array.isArray(data) ? data : (data?.candidatos ?? []);
  return { lunes, candidatos };
};

/**
 * Activa o quita la visibilidad de una encuesta F4 para Mystery (contrato §4). 204 sin cuerpo.
 * Lo que se quita a mano no se reactiva solo (`BT_QUITADA_MANUAL_CX`).
 */
export const setVisibilidadF4 = async (idEncuestaRealizada: number, visible: boolean): Promise<void> => {
  await api.post(`/api/cx/encuestas-f4/${idEncuestaRealizada}/visibilidad`, { visible });
};

/** Lo que Brevo sabe de una campaña (solo lectura). */
export interface CampanaBrevo {
  id: number;
  nombre: string;
  /** draft | sent | queued | suspended | in_process | archive */
  estado?: string | null;
  fechaCreacion?: string | null;
  fechaEnvio?: string | null;
  /** Contactos de sus listas (antes de enviar) o enviados (después). */
  destinatarios?: number | null;
  entregados?: number | null;
  aperturas?: number | null;
  clics?: number | null;
  bajas?: number | null;
  rebotes?: number | null;
}

/** Campaña de la semana de un tipo: nombre esperado y, si existe, su estado en Brevo. */
export interface CampanaTipoSemana {
  tipo: TipoCompra;
  /** `ROC_S_37-2026`: semana ISO de los datos, como la crea el flujo n8n. */
  nombre: string;
  existe: boolean;
  brevo?: CampanaBrevo | null;
}

export interface CampanasSemana {
  semana: number;
  anio: number;
  /** false: no hay clave de Brevo configurada; solo vienen los nombres. */
  brevoConfigurado: boolean;
  errorBrevo?: string | null;
  campanas: CampanaTipoSemana[];
}

/** Campañas de Brevo de la semana `lunes` → `lunes+6`, para cotejar con lo calculado. Solo lectura. */
export const getCampanasSemana = async (lunes: string): Promise<CampanasSemana> => {
  const { data } = await api.get<CampanasSemana>(`/api/cx/panel/campanas?lunes=${encodeURIComponent(lunes)}`);
  return data;
};

/** Una encuesta F4 con su visibilidad para Mystery (vista «Mystery (F4)»). */
export interface EncuestaF4 {
  id: number;
  fechaCreacion?: string | null;
  plazoFin?: string | null;
  fechaRespuesta?: string | null;
  codigoCliente?: string | null;
  nombreCliente?: string | null;
  ccl?: string | null;
  nombreOperacion?: string | null;
  /** Vendedor | Comprador | el nombre del catálogo para el resto (p. ej. «Cliente de gestión»). */
  rol?: string | null;
  /** 953 Pendiente · 954 Iniciada · 955 Terminada sin respuesta (no elegible) · 956 Terminada con respuesta · 957 Terminada sin respuesta */
  estadoCod: number;
  visibleMystery: boolean;
  quitadaManual: boolean;
  llamadasSinExito: number;
  /** Inicio efectivo del plazo: el que fijó CxCp (o la F4 automática al hacerla visible) o, si no hay, la creación. */
  plazoInicio?: string | null;
  /** «Notas FCT (compartidas con Mystery Consulting)»: las escribe CxCp y Mystery las lee en su portal. */
  notasFct?: string | null;
  /** «Mystery Consulting · Notas»: las escribe Mystery en su portal. Solo lectura desde aquí. */
  notasMystery?: string | null;
  /** Operación de la F4: agrupa sus clientes en una fila. */
  idOperacion?: number | null;
  /** Asesor de la operación. */
  asesor?: string | null;
  /** Venta | Gestión. */
  tipoOperacion?: string | null;
}

/** Todas las encuestas F4 (cualquier versión), con su visibilidad para Mystery y su ficha (plazo y notas). */
export const getEncuestasF4 = async (): Promise<EncuestaF4[]> => {
  const { data } = await api.get<EncuestaF4[]>('/api/cx/panel/f4');
  return Array.isArray(data) ? data : [];
};

/** Un cliente de una operación que está hoy en Fase 4 y no tiene encuesta F4 («[sin encuesta]» de AuraQuantic). */
export interface SinEncuestaF4 {
  idOperacion: number;
  ccl?: string | null;
  nombreOperacion?: string | null;
  asesor?: string | null;
  tipoOperacion?: string | null;
  /** Entrada en Fase 4 (fecha real de la firma), YYYY-MM-DD. */
  fechaEntradaF4?: string | null;
  codigoCliente?: string | null;
  nombreCliente?: string | null;
  rol?: string | null;
}

/** Clientes de las operaciones en Fase 4 que no tienen encuesta F4. */
export const getF4SinEncuesta = async (): Promise<SinEncuestaF4[]> => {
  const { data } = await api.get<SinEncuestaF4[]>('/api/cx/panel/f4/sin-encuesta');
  return Array.isArray(data) ? data : [];
};

/**
 * Lo que se guarda de la ficha de una F4 (contrato §9.4). Es un PUT: REEMPLAZA los tres campos, así que
 * para no cambiar uno hay que reenviar el valor que ya tiene. `null` en notas las deja vacías; en el
 * inicio vuelve a valer la fecha de creación.
 */
export interface DatosFichaF4 {
  notasFct: string | null;
  /** Día de Madrid, YYYY-MM-DD. */
  plazoInicio: string | null;
  /** Día de Madrid, YYYY-MM-DD. No puede ser anterior al inicio (solo se comprueba si el plazo cambia). */
  plazoFin: string | null;
}

/** La ficha ya guardada (mismos nombres que el listado, para actualizar la fila con ella). */
export interface FichaF4 {
  id: number;
  notasFct?: string | null;
  notasMystery?: string | null;
  plazoInicio?: string | null;
  plazoFin?: string | null;
}

/** Guarda las notas FCT y el plazo de una F4. Permiso: el de la visibilidad F4 (`Cx.EncuestasF4.Visibilidad.Write`). */
export const guardarFichaF4 = async (id: number, datos: DatosFichaF4): Promise<FichaF4> => {
  const { data } = await api.put<FichaF4>(`/api/cx/panel/f4/${id}/ficha`, datos);
  return data;
};

/** Código corto del tipo de encuesta (calculado en SQL a partir de la encuesta). */
export type TipoEncuestaRegistrada = 'ROC' | 'OC' | 'SI' | 'OV' | 'F2' | 'F0' | 'CIV' | 'F4' | 'OTRA';

/** Una encuesta registrada (fila de `GC_ENCUESTA_REALIZADA`, de cualquier encuesta). Vista «Registradas». */
export interface EncuestaRegistrada {
  idEncuestaRealizada: number;
  /** Fecha de la encuesta: la del envío y, si no consta, la de creación (ISO 8601). */
  fechaEncuesta?: string | null;
  /** Fecha en que se respondió (ISO 8601). */
  fechaRespuesta?: string | null;
  idEncuesta: number;
  /** Nombre de la encuesta (`GC_ENCUESTA.DS_ENCUESTA`). */
  nombreEncuesta?: string | null;
  tipo: TipoEncuestaRegistrada | string;
  idCliente: number;
  codigoCliente?: string | null;
  nombreCliente?: string | null;
  idOperacion?: number | null;
  /** CCL de la operación (`GC_OPERACION.DS_CCL`). */
  ccl?: string | null;
  /** Nombre de la operación (`GC_OPERACION.DS_ALIAS`). */
  nombreOperacion?: string | null;
  /** Parte del cliente en la operación: Vendedor | Comprador. */
  rol?: string | null;
  respondida: boolean;
  numRespuestas: number;
  /** Solo F4: visible para Mystery (`BT_GESTIONAR`). `null` en el resto de tipos. */
  visibleMystery?: boolean | null;
  /** Solo F4: la visibilidad se quitó a mano (`BT_QUITADA_MANUAL_CX`) y no se reactiva sola. */
  quitadaManual?: boolean | null;
  /** OC/SI: responsable de la oportunidad de compra; ROC: responsable de la reunión ROC. */
  asesor?: string | null;
  /** OC/SI: farmacia (o farmacias, separadas por « · ») de las operaciones de la oportunidad. */
  farmacia?: string | null;
  /** Nota del NPS (0-10) si la encuesta la tiene; null si no. */
  nps?: number | null;
}

/** Una respuesta de una encuesta registrada. */
export interface RespuestaEncuesta {
  /** Orden de la pregunta en el cuestionario. */
  orden?: number | null;
  /** Código corto de la pregunta (`GC_PREGUNTA.DS_PREGUNTA`, p. ej. `ROC_Recomendacion`), no el enunciado. */
  pregunta: string;
  respuesta?: string | null;
}

/** Todas las encuestas registradas con fecha entre `desde` y `hasta` (YYYY-MM-DD, ambos inclusive). Máximo 366 días. */
export const getEncuestasRegistradas = async (desde: string, hasta: string): Promise<EncuestaRegistrada[]> => {
  const { data } = await api.get<EncuestaRegistrada[]>(
    `/api/cx/panel/registradas?desde=${encodeURIComponent(desde)}&hasta=${encodeURIComponent(hasta)}`,
  );
  return Array.isArray(data) ? data : [];
};

/** Respuestas de una encuesta registrada, en el orden del cuestionario. Solo lectura. */
export const getRespuestasEncuesta = async (idEncuestaRealizada: number): Promise<RespuestaEncuesta[]> => {
  const { data } = await api.get<RespuestaEncuesta[]>(`/api/cx/panel/registradas/${idEncuestaRealizada}/respuestas`);
  return Array.isArray(data) ? data : [];
};

// ---------------------------------------------------------------------------------------------
// Bandeja «Pendientes de envío» (contrato §8.4, D-CX-68): envíos por evento que el sistema prepara
// solo cada día y que CxCp aprueba o descarta. Leer = `Cx.Panel.Read`; aprobar y descartar =
// `Cx.Bandeja.Write` (solo CxCp).
// ---------------------------------------------------------------------------------------------

/** V2 área privada · F2 encuesta F2 · F4O F4 online. V0 (oportunidad de venta) y V1 (Bienvenida F1) solo en el
 *  histórico: se retiraron de Encuestas CX (D-CX-84). */
export type TipoBandeja = 'V0' | 'V1' | 'V2' | 'F2' | 'F4O';

/** Estado deducido de cada elemento (lo calcula el backend). */
export type EstadoBandeja = 'PENDIENTE' | 'BLOQUEADO' | 'APROBADO' | 'ENVIADO' | 'DESCARTADO';

/** Lo que se pide: lo que aún requiere atención o el histórico de enviados y descartados. */
export type VistaBandeja = 'abiertas' | 'cerradas';

/** Equipo de la operación (solo V1 y V2): también dice a quién avisar si está bloqueado. */
export interface EquipoOperacion {
  asesor?: string | null;
  analista?: string | null;
  juridico?: string | null;
  /** «Equipo Celular»: nulo hasta que se confirme su rol en Gescom (A-22). */
  ecox?: string | null;
}

/** Un correo preparado de la bandeja. No lleva el token del envío (solo lo ve n8n). */
export interface ElementoBandeja {
  id: number;
  tipo: TipoBandeja | string;
  estado: EstadoBandeja | string;
  idCliente: number;
  codigoCliente?: string | null;
  nombre?: string | null;
  /** Email REAL del cliente. Con `modoPrueba` el correo sale en realidad a las direcciones de prueba. */
  email?: string | null;
  idOperacion?: number | null;
  ccl?: string | null;
  nombreOperacion?: string | null;
  /** Solo F2: día del ciclo (15, 45, 90, 135…). */
  ciclo?: number | null;
  plantillaId?: number | null;
  /** BLOQUEADO: qué falta («Falta: analista, jurídico»). DESCARTADO: el motivo («AUTO: …» si fue solo). */
  motivo?: string | null;
  creadoEl?: string | null;
  aprobadoEl?: string | null;
  aprobadoPor?: string | null;
  enviadoEl?: string | null;
  descartadoEl?: string | null;
  equipo?: EquipoOperacion | null;
}

export interface BandejaListado {
  /** true: los correos salen a las direcciones de prueba, no al cliente (el panel lo avisa). */
  modoPrueba: boolean;
  /** Con modo prueba, los buzones a los que salen de verdad los correos (en rueda). Vacío sin modo prueba. */
  destinatariosPrueba: string[];
  items: ElementoBandeja[];
}

export interface FiltroBandeja {
  estado: VistaBandeja;
  /** Opcional: solo un tipo. */
  tipo?: TipoBandeja;
  /** Solo con `cerradas` (YYYY-MM-DD, ambos inclusive; por defecto los últimos 30 días, máximo 366). */
  desde?: string;
  hasta?: string;
}

/** La bandeja: lo abierto (pendiente, bloqueado, aprobado sin enviar) o lo cerrado (enviado, descartado). */
export const getBandeja = async (filtro: FiltroBandeja): Promise<BandejaListado> => {
  const params = new URLSearchParams({ estado: filtro.estado });
  if (filtro.tipo) params.set('tipo', filtro.tipo);
  if (filtro.estado === 'cerradas') {
    if (filtro.desde) params.set('desde', filtro.desde);
    if (filtro.hasta) params.set('hasta', filtro.hasta);
  }
  const { data } = await api.get<Partial<BandejaListado>>(`/api/cx/panel/bandeja?${params.toString()}`);
  return {
    // Solo se avisa del modo prueba si el backend lo dice expresamente: avisarlo sin serlo haría creer que
    // un correo real no le llega al cliente.
    modoPrueba: data?.modoPrueba === true,
    destinatariosPrueba: data?.modoPrueba === true && Array.isArray(data?.destinatariosPrueba) ? data!.destinatariosPrueba : [],
    items: Array.isArray(data?.items) ? data!.items : [],
  };
};

/** Resultado de aprobar: lo aprobado, lo que sigue bloqueado y lo que ya no se podía aprobar. */
export interface ResultadoAprobar {
  aprobados: number;
  /** Pedidos que siguen sin aprobar: bloqueados (falta algo) o cuyo cliente se quedó sin email. */
  bloqueados: number;
  /** Ya no aprobables: no existen, ya aprobados, enviados o descartados. */
  ignorados: number;
}

/** Qué aprobar: unos concretos o todos los pendientes (opcionalmente de un tipo). */
export type PeticionAprobar = { ids: number[] } | { todos: true; tipo?: TipoBandeja };

/** Aprueba envíos: saldrán en el próximo envío de n8n. Solo se aprueba lo PENDIENTE. */
export const aprobarBandeja = async (peticion: PeticionAprobar): Promise<ResultadoAprobar> => {
  const { data } = await api.post<ResultadoAprobar>('/api/cx/panel/bandeja/aprobar', peticion);
  return { aprobados: data?.aprobados ?? 0, bloqueados: data?.bloqueados ?? 0, ignorados: data?.ignorados ?? 0 };
};

export interface ResultadoDescartar {
  descartados: number;
}

/** Descarta envíos con un motivo (3 a 200 caracteres). Lo descartado no se vuelve a preparar. */
export const descartarBandeja = async (ids: number[], motivo: string): Promise<ResultadoDescartar> => {
  const { data } = await api.post<ResultadoDescartar>('/api/cx/panel/bandeja/descartar', { ids, motivo });
  return { descartados: data?.descartados ?? 0 };
};
